import { inject } from '@angular/core';
import { Observable, catchError, map, of } from 'rxjs';
import {
	CanActivateFn,
	Router,
	RouterStateSnapshot,
	ActivatedRouteSnapshot,
} from '@angular/router';
import { AuthService } from './auth.service';
import { Routing } from '@enums/routing.enum';
import { moduleForUrl } from '@core/main-layout/cms/nav.config';
import { canAccessModule } from '@core/main-layout/cms/module-access.config';

/**
 * Strażnik modułów: zakładka, do której użytkownik nie ma dostępu, przekierowuje na ekran
 * odblokowania (`/upgrade?module=X`). O dostępie mówi lista `modules` z serwera
 * (`canAccessModule`) — ta sama, którą czyta menu, więc zakładka nigdy nie wygląda na otwartą,
 * gdy strażnik ją zamknie. Admin ma wszystko. Dane i tak chroni backend (`module_gate`).
 */
export const moduleGuard: CanActivateFn = (
	_route: ActivatedRouteSnapshot,
	state: RouterStateSnapshot,
): boolean | Observable<boolean> => {
	const auth = inject(AuthService);
	const router = inject(Router);
	const module = moduleForUrl(state.url);

	const decide = (): boolean => {
		if (canAccessModule(auth.user(), module)) {
			return true;
		}

		router.navigate([Routing.PanelUpgrade], { queryParams: { module } });
		return false;
	};

	// Sesja sprzed zmiany modelu nie ma listy modułów — dociągamy ją, zanim cokolwiek
	// zablokujemy (inaczej użytkownik z odblokowanym modułem trafiłby na ekran odblokowania).
	if (auth.user() && auth.user()!.modules === undefined) {
		return auth.getMe().pipe(
			map(() => decide()),
			catchError(() => of(decide())),
		);
	}

	return decide();
};
